import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";

import AddTaxesModal from "./AddTaxesModal";
import EditTaxesModal from "./EditTaxesModal";
import { useAuthDetails } from "../../../Common/cookiesHelper";
import { fetchtaxesData, deleteTax } from "../../../Redux/features/Taxes/taxesSlice";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import DeleteModal from "../../../reuseableComponents/DeleteModal";
import SortModal from "../../../reuseableComponents/SortModal";
import PasswordShow from "../../../Common/passwordShow";
import { BASE_URL, TAXE_CATEGORY_LIST, SORT_CATOGRY_DATA } from "../../../Constants/Config";
import Icon from "../../NewInventory/Icon";
import { TaxDeleteIcon } from "./TaxActionIcons";
import useStickyBelowLayoutHeader from "../../../hooks/useStickyBelowLayoutHeader";
import useDragAutoScroll from "../../../hooks/useDragAutoScroll";
import { ciUrl } from "../../../Constants/nodeCi";
import "../../../Styles/TaxesWorkspace.css";

const formatTaxRate = (item) => {
  const value = item?.percent ?? "";
  if (item?.type === "1") return `$${value}`;
  if (item?.type === "0") return `${value}%`;
  return value === "" ? "—" : `${value}%`;
};

const generateSortKey = (index) => {
  const alphabet = "abcdefghijklmnopqrstuvwxyz";
  let value = "";
  let i = index + 26;
  while (i >= 0) {
    value = alphabet[i % 26] + value;
    i = Math.floor(i / 26) - 1;
  }
  return value;
};

const TaxesDetail = () => {
  const [alltaxes, setalltaxes] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const { LoginGetDashBoardRecordJson, userTypeData } = useAuthDetails();
  const AlltaxesDataState = useSelector((state) => state.taxes);
  const dispatch = useDispatch();
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const stickyTop = useStickyBelowLayoutHeader();

  const { handleCoockieExpire, getUnAutherisedTokenMessage, getNetworkError } =
    PasswordShow();

  useEffect(() => {
    getfetchtaxesDataData();
  }, []);

  const getfetchtaxesDataData = async () => {
    try {
      const data = {
        merchant_id: merchant_id,
        ...userTypeData,
      };
      if (data) {
        await dispatch(fetchtaxesData(data)).unwrap();
      }
    } catch (error) {
      if (error?.status == 401 || error?.response?.status === 401) {
        getUnAutherisedTokenMessage();
        handleCoockieExpire();
      } else if (error?.status == "Network Error") {
        getNetworkError();
      }
    }
  };

  useEffect(() => {
    if (!AlltaxesDataState.loading && AlltaxesDataState.taxesData) {
      setalltaxes(AlltaxesDataState.taxesData);
    }
  }, [
    AlltaxesDataState,
    AlltaxesDataState.loading,
    AlltaxesDataState.taxesData,
  ]);

  useEffect(() => {
    if (!merchant_id) return;

    const fetchCategories = async () => {
      const formData = new FormData();
      formData.append("merchant_id", merchant_id);
      formData.append("token_id", userTypeData?.token_id);
      formData.append("login_type", userTypeData?.login_type);
      try {
        const response = await axios.post(
          ciUrl(TAXE_CATEGORY_LIST),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${userTypeData?.token}`,
            },
          },
        );
        const categoryList = response.data?.result || [];
        setCategoryOptions(
          categoryList.map((category) => ({
            id: category.id,
            title: category.title,
          })),
        );
      } catch (error) {
        if (error?.status == 401 || error?.response?.status === 401) {
          getUnAutherisedTokenMessage();
          handleCoockieExpire();
        } else if (error?.status == "Network Error") {
          getNetworkError();
        }
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [merchant_id]);

  const [deleteTaxeId, setDeleteTaxeId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [sortPayload, setSortPayload] = useState({});
  const [sortBackup, setSortBackup] = useState([]);
  const [sortLoading, setSortLoading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const listRef = useRef(null);

  // Scroll the page while a tax is held near the top/bottom edge, so it can
  // be dropped anywhere in a list longer than the viewport.
  useDragAutoScroll(dragId !== null, listRef, { topOffset: stickyTop });

  const handleDeleteTax = (id) => {
    setDeleteTaxeId(id);
    setDeleteModalOpen(true);
  };

  const confirmDeleteCategory = () => {
    if (deleteTaxeId) {
      const data = {
        id: deleteTaxeId,
        merchant_id: merchant_id,
        login_employee_id: LoginGetDashBoardRecordJson?.employee_id
          ? LoginGetDashBoardRecordJson?.employee_id
          : null,
        ...userTypeData,
      };
      if (data) {
        dispatch(deleteTax(data));
        ToastifyAlert("Deleted Successfully", "success");
      }
    }
    setDeleteTaxeId(null);
    setDeleteModalOpen(false);
  };

  const clearDrag = () => {
    setDragId(null);
    setDragOverId(null);
  };

  const onRowDragStart = (e, id) => {
    if (e.target.closest("button, a, input")) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
    setDragId(String(id));
  };

  const onRowDragOver = (e, id) => {
    if (!dragId || dragId === String(id)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const halfway = rect.top + rect.height / 2;
    setDragOverId({
      id: String(id),
      position: e.clientY < halfway ? "above" : "below",
    });
  };

  const onRowDrop = (e) => {
    e.preventDefault();
    if (!dragId || !dragOverId) {
      clearDrag();
      return;
    }
    const list = [...alltaxes];
    const fromIdx = list.findIndex((tax) => String(tax?.id) === dragId);
    let toIdx = list.findIndex((tax) => String(tax?.id) === dragOverId.id);
    if (fromIdx < 0 || toIdx < 0) {
      clearDrag();
      return;
    }
    const [moved] = list.splice(fromIdx, 1);
    if (fromIdx < toIdx) toIdx -= 1;
    if (dragOverId.position === "below") toIdx += 1;
    list.splice(toIdx, 0, moved);

    const unchanged = list.every(
      (tax, index) => String(tax?.id) === String(alltaxes[index]?.id),
    );
    clearDrag();
    if (unchanged) return;

    const values = {};
    list.forEach((tax, index) => {
      values[`values[${tax?.id}]`] = generateSortKey(index);
    });
    setSortBackup(alltaxes);
    setSortPayload(values);
    setalltaxes(list);
    setSortModalOpen(true);
  };

  const confirmSort = async () => {
    setSortLoading(true);
    try {
      await axios.post(
        BASE_URL + SORT_CATOGRY_DATA,
        {
          table: "taxes",
          merchant_id,
          token_id: userTypeData?.token_id,
          login_type: userTypeData?.login_type,
          session_id: userTypeData?.session_id || "",
          login_employee_id: LoginGetDashBoardRecordJson?.employee_id || null,
          ...sortPayload,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userTypeData?.token}`,
          },
        },
      );
    } catch (error) {
      setalltaxes(sortBackup);
      console.error("API call failed:", error);
    } finally {
      setSortLoading(false);
      setSortModalOpen(false);
      setSortPayload({});
    }
  };

  const loading = AlltaxesDataState.loading;

  return (
    <div className="qv-taxes-page" data-mwskip>
      <div className="qv-taxes-header">
        <div className="qv-taxes-header__title">
          <h1>Taxes</h1>
          <p>Set rates and apply them to product categories</p>
        </div>
        <div className="qv-taxes-header__actions">
          <AddTaxesModal
            categoryOptions={categoryOptions}
            loadingCategories={loadingCategories}
          />
        </div>
      </div>

      <div
        ref={listRef}
        className="qv-taxes-table-card"
        style={{ "--qv-taxes-sticky-top": `${stickyTop}px` }}
      >
        <div className="qv-taxes-list-head">
          <span className="qv-taxes-col-sort">Sort</span>
          <span>Title</span>
          <span className="qv-taxes-col-rate">Percentage (%)</span>
          <span className="qv-taxes-col-actions">Actions</span>
        </div>
        {loading ? (
          [1, 2, 3, 4, 5].map((row) => (
            <div className="qv-taxes-row" key={row}>
              <span className="qv-taxes-col-sort">
                <Skeleton width={20} height={20} />
              </span>
              <span>
                <Skeleton width="60%" />
              </span>
              <span className="qv-taxes-col-rate">
                <Skeleton width={72} />
              </span>
              <span className="qv-taxes-col-actions">
                <Skeleton width={56} height={28} />
              </span>
            </div>
          ))
        ) : alltaxes?.length >= 1 ? (
          alltaxes.map((item) => {
            const isDefault = item?.title === "DefaultTax";
            const id = String(item?.id);
            const isDragging = dragId === id;
            const overPos = dragOverId?.id === id ? dragOverId.position : null;
            return (
              <div
                key={id}
                className={[
                  "qv-taxes-row",
                  isDragging ? "is-dragging" : "",
                  overPos === "above" ? "is-over-above" : "",
                  overPos === "below" ? "is-over-below" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                draggable
                onDragStart={(e) => onRowDragStart(e, id)}
                onDragEnd={clearDrag}
                onDragOver={(e) => onRowDragOver(e, id)}
                onDrop={onRowDrop}
              >
                <span className="qv-taxes-col-sort">
                  <span className="qv-taxes-drag" title="Drag to reorder">
                    <Icon name="grip" size={16} />
                  </span>
                </span>
                <span className="qv-taxes-col-title">{item?.title}</span>
                <span className="qv-taxes-col-rate">{formatTaxRate(item)}</span>
                <span className="qv-taxes-col-actions">
                  <div className="qv-taxes-actions">
                    <EditTaxesModal
                      selectedTaxe={item}
                      categoryOptions={categoryOptions}
                      loadingCategories={loadingCategories}
                    />
                    {!isDefault ? (
                      <button
                        type="button"
                        className="qv-taxes-icon-btn qv-taxes-icon-btn--danger"
                        onClick={() => handleDeleteTax(item?.id)}
                        aria-label={`Delete ${item?.title}`}
                        title="Delete tax"
                      >
                        <TaxDeleteIcon />
                      </button>
                    ) : null}
                  </div>
                </span>
              </div>
            );
          })
        ) : (
          <div className="qv-taxes-empty">No taxes configured</div>
        )}
      </div>

      <DeleteModal
        modalClassName="qv-taxes-confirm"
        headerText="Tax"
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDeleteCategory}
      />
      <SortModal
        modalClassName="qv-taxes-confirm"
        headerText="sort items"
        open={sortModalOpen}
        onClose={() => {
          setSortModalOpen(false);
          setalltaxes(sortBackup);
        }}
        onConfirm={confirmSort}
        sortModeConfirmedLoading={sortLoading}
      />
    </div>
  );
};

export default TaxesDetail;
